import { commonRoutes } from '@/app/providers/router/routes';

export const REFERRAL_CODE_MIN_LENGTH = 3;
export const REFERRAL_CODE_MAX_LENGTH = 20;
export const REFERRAL_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]*$/;

const toReferralCode = (value: string): string =>
  value.trim().toUpperCase().replace(/\s+/g, '').slice(0, REFERRAL_CODE_MAX_LENGTH);

const getCodeFromUrl = (value: string): string | null => {
  try {
    return new URL(value).searchParams.get('code');
  } catch {
    return null;
  }
};

export const normalizeReferralCode = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const queryMatch = trimmed.match(/[?&]code=([^&\s#]+)/i);
  if (queryMatch?.[1]) {
    return toReferralCode(decodeURIComponent(queryMatch[1]));
  }

  if (/^https?:\/\//i.test(trimmed)) {
    const codeFromUrl = getCodeFromUrl(trimmed);
    if (codeFromUrl) return toReferralCode(codeFromUrl);
  }

  return toReferralCode(trimmed);
};

export const isValidReferralCodeFormat = (code: string): boolean => {
  const normalized = normalizeReferralCode(code);
  if (normalized.length < REFERRAL_CODE_MIN_LENGTH) return false;
  return REFERRAL_CODE_PATTERN.test(normalized);
};

export const isOwnReferralCode = (code: string, ownReferralCode?: string | null): boolean => {
  if (!ownReferralCode) return false;
  return normalizeReferralCode(code) === normalizeReferralCode(ownReferralCode);
};

export const buildReferralLink = (code: string): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${commonRoutes.REFERRAL}?code=${normalizeReferralCode(code)}`;
};

export const buildShareOnXUrl = (referralLink: string): string => {
  const text = 'Join Mezzanine with my referral link:';
  const params = new URLSearchParams({ text, url: referralLink });
  return `https://twitter.com/intent/tweet?${params.toString()}`;
};

export const REFERRAL_ERRORS = {
  INVALID_CODE: 'Invalid referral code. Please check and try again.',
  CODE_TAKEN: 'Code is taken, try another',
  INVALID_FORMAT: 'Invalid code format. Use A–Z, 0–9, -, _',
  SELF_REFERRAL: 'You cannot use your own referral code.',
  CODE_UNAVAILABLE: 'This code is not available.',
} as const;
